/**
 * TmaContext — Telegram Mini App lifecycle state — L&D Super-UX Phase 6 PR 6.1.
 *
 * Singleton, инициализируется на старте app, если `isInTelegramWebApp()`:
 *   1. Зовёт `WebApp.ready()` + `expand()` чтобы Telegram client убрал loader.
 *   2. Если в localStorage уже есть валидный JWT — пропускаем TMA login.
 *   3. Иначе берём `initData`, шлём в `/auth/telegram-webapp`, кладём JWT.
 *      AuthGate в main.jsx подхватит токен и зарендерит TmaShell.
 *   4. На ошибку (initData пустой / 401 / 403 / сеть) — экран отказа
 *      с «Повторить» и «Закрыть».
 *   5. 401 посреди работы — тихий перевход свежим initData и повтор запроса.
 *
 * Не дублирует AuthContext — только мост между Telegram SDK и нашим JWT.
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react'

import { apiTelegramWebAppLogin } from '../api/auth'
import { getToken, setUnauthorizedRecovery } from '../api/core'
import useTmaBrandTheme from '../utils/tmaBrandTheme'
import {
    getInitData,
    getTelegramSdk,
    isInTelegramWebApp,
    readyTelegramApp,
} from '../utils/telegramWebApp'

const TmaContext = createContext({
    isTma: false,
    status: 'idle', // 'idle' | 'authenticating' | 'authenticated' | 'error'
    error: null,
    retry: () => {},
})

/**
 * Отказ входа по initData → вид экрана отказа.
 *   denied        — 401/403: сервер не узнал сотрудника (нет связи Telegram,
 *                   учётка отключена, подпись не принята);
 *   org_suspended — 403 с приостановленной организацией: писать HR бесполезно;
 *   network       — нет связи, таймаут или 5xx: помогает «Повторить».
 */
export function classifyTmaLoginError(err) {
    const status = err && err.status
    const msg = (err && err.message) || ''
    if (msg.includes('organization_suspended')) return { kind: 'org_suspended' }
    if (status === 401 || status === 403) return { kind: 'denied', reason: msg }
    return { kind: 'network', message: msg }
}

export function TmaProvider({ children }) {
    const [status, setStatus] = useState('idle')
    const [error, setError] = useState(null)
    const [isTma] = useState(() => isInTelegramWebApp())
    // Палитра Onboardix по теме телефона и шапка Telegram в её цвет — с
    // первого кадра, ещё до входа: экран «Подключаюсь…» уже в нужной теме.
    useTmaBrandTheme(isTma)

    // Вход подписью Telegram. `silent` — перевход посреди работы: экран не
    // мигает «Подключаюсь…», сотрудник видит только результат повторённого
    // запроса. Возвращает true, если токен положен.
    const signIn = useCallback(async ({ silent = false } = {}) => {
        const initData = getInitData()
        if (!initData) {
            // `?tma=1` flag без real Telegram client → нет initData для backend.
            // Просим открыть через бота. Пользователь увидит понятный экран.
            setStatus('error')
            setError({ kind: 'missing_init_data' })
            return false
        }
        if (!silent) setStatus('authenticating')
        setError(null)
        try {
            await apiTelegramWebAppLogin(initData)
            setStatus('authenticated')
            // Trigger re-render of AuthGate by dispatching custom event;
            // AuthContext подписан, прочитает свежий JWT и подтянет /auth/me.
            if (!silent) window.dispatchEvent(new Event('auth:refresh'))
            return true
        } catch (err) {
            setError(classifyTmaLoginError(err))
            setStatus('error')
            return false
        }
    }, [])

    const performLogin = useCallback(() => signIn(), [signIn])

    // 401 посреди работы (токен живёт сутки) — один тихий перевход свежим
    // initData и повтор исходного запроса, см. setUnauthorizedRecovery в
    // api/core.js. Истёкший токен при повторном открытии лечится тем же путём:
    // первый же запрос с ним получит 401.
    useEffect(() => {
        if (!isTma) return undefined
        return setUnauthorizedRecovery(() => signIn({ silent: true }))
    }, [isTma, signIn])

    useEffect(() => {
        if (!isTma) return
        // Telegram client expects us to call ready() ASAP — иначе он держит
        // loading-overlay поверх iframe.
        readyTelegramApp()
        // Telegram WebApp поднимает viewport-height до full screen
        // только после expand() — выполнено в readyTelegramApp().
        if (getToken()) {
            // Уже залогинены (предыдущая сессия Mini App). Пропускаем
            // /auth/telegram-webapp вызов — это нормальный flow для return-visit.
            // Если токен протух, первый же запрос перевойдёт молча.
            setStatus('authenticated')
            return
        }
        performLogin()
    }, [isTma, performLogin])

    const value = {
        isTma,
        status,
        error,
        retry: performLogin,
        sdk: getTelegramSdk(),
    }

    return <TmaContext.Provider value={value}>{children}</TmaContext.Provider>
}

export function useTma() {
    return useContext(TmaContext)
}
